'use strict';
/* ==========================================================================
   core-local/circle.js — "Round and Round": the circle model (DESIGN.md §4).
   Pure: no DOM, no engine globals. Runs in node (module.exports: tools/circlecheck.js, tools/contentcheck.js) and in
   the page (window.Circle). Registers itself with the question bank as `circle` when core/bank.js is loaded first
   (lesson.json scripts order), so pool / labs.json formulas can say `circle.A314(6)`.

   UNITS: whatever the caller uses (inches, feet, "diameters"); every function is unit-free.
   COORDINATES (mark, sectors, layout, gridCount): y points DOWN (canvas convention), angles in radians, a positive
   rotation turns clockwise on screen. A drawer maps a model point (x, y) to (ox + x·k, oy + y·k) for its scale k.

   ---- numbers ----
   Circle.PI = Math.PI;  Circle.PI314 = 3.14 (the rounded stand-in for typed answers, facts §1)
   C(d) = π·d, A(r) = π·r²                                   exact floats
   C314(d), A314(r)                                          3.14 × d, 3.14 × r × r, rounded to hundredths (half away
                                                             from zero) in integer hundredths (no float drift): 113.04
   C314h(d), A314h(r)                                        the same in integer hundredths (11304)
   Cpi(d), Api(r)                                            π·d, π·r² with the full π (the π key), rounded to
                                                             hundredths: 37.7, 254.47 (facts row 4.5: a checker
                                                             accepts the approximation the question names)
   Cpih(d), Apih(r)                                          the same in integer hundredths (3770, 25447)
   inPi(k) → "36π" (k trimmed to ≤ 2 decimals; 1 → "π", 0 → "0")   (no notebook face has π: the lesson's own
                                                             one-glyph fonts, core-local/pifont.js, render it; DESIGN.md §6)
   half(d) = d / 2, twice(r) = 2·r, sq(x) = x·x
   num(v) → "12.5" (≤ 2 decimals, zeros trimmed, U+2212 minus), fix2(v) → "113.04" (exactly 2 decimals)
   digits(v, n) → v to n decimals, as text ("3.14159265")
   hundredths(x) → the integer hundredths of x, half away from zero (37.699 → 3770)
   ---- rolling without slipping (facts row 4.1) ----
   turn(d) = π·d                       the floor distance of one full turn
   angle(d, s) = 2s / d                radians turned after rolling s (clockwise on screen when rolling to +x)
   mark(d, s) → {x, y, cx, cy, angle, onFloor}
                                       the paint spot, which touches the floor at x = 0 when s = 0. The hub is at
                                       (cx, cy) = (s, −r) (floor at y = 0, y down); the spot (a cycloid point) is at
                                       x = s − r·sin θ, y = −r + r·cos θ (θ = angle(d, s)); onFloor: |y| < 1e-9.
                                       Draw the wheel at the hub rotated by +θ with its spot at local (0, r).
   stamps(d, s) → [0, πd, 2πd, …]      the floor x of every stamp made so far (s ≥ 0; the start stamp at 0 included)
   sticks(d) → {whole: 3, rest, restLength, turn, d}
                                       whole diameters in one turn (3), the leftover as a fraction of a diameter
                                       (π − 3 = 0.14159…) and as a length ((π − 3)·d)
   ---- the shelf (docs/topics/facts-circles.md §6) ----
   objects → [{id, name, short, d, unit, unitName, units, row, perSquare, px, scaleText, invented, nominal, ball}]
             in shelf order: quarter (0.955 in, row 6.1), pizza (12 in, an invented example; "12-inch" = the diameter,
             row 6.15), rim (a basketball rim, 18 in inside, row 6.2), spaceship (Spaceship Earth at EPCOT, a sphere
             165 ft across, row 6.11; ball: true = roll it as a ball, drawn as a circle with a triangle-panel hatch).
             d = the diameter in `unit`; row = the facts-file row; perSquare = units per 40-px grid square at the
             drawing scale, px = logical px per unit (= 40 / perSquare), scaleText = "1 square = 2 inches".
   wheel → {id: 'bike', name: 'a 26-inch bike wheel', d: 26, unit: 'in', row: '6.14, 4.2', nominal: true, …}
             the first roll's bike wheel (a nominal size name: FLDOE's own 26-inch wheel), NOT on the shelf
   obj(id) → a shelf object or the bike wheel ('bike'); D(id) → its diameter (formulas: circle.D('pizza'))
   row(obj, {ratioDigits = 2}) → {across: "12 in", around: "37.70 in", ratio: "3.14", ratio4: "3.1416",
                                  values: {across, around, ratio}}  (around = πd to 2 decimals; ratio = around ÷ across
                                  computed from the UNROUNDED values, so it is π every time)
   ---- sectors: slice and unroll (DESIGN.md §5.2) ----
   sectors(r, n) → [{i, a0, a1, mid, half, area}]   n even ≥ 2. Sector i spans the screen angles [a0, a1] =
                                       [−π + 2i·h, −π + 2(i+1)·h] (h = π/n): i < n/2 is the top half (left → right),
                                       i ≥ n/2 the bottom half (right → left). area = h·r² (they add up to πr²).
   shape(r, n, {arc = 24}) → {half, apex: [0, 0], left: [x, y], right: [x, y], outline: [[x, y] …], crust: [[x, y] …]}
                                       ONE sector in its LOCAL frame: apex at the origin, crust UP (toward −y), the
                                       two straight edges to left = (−r·sin h, −r·cos h) and right = (r·sin h, −r·cos h).
                                       outline = apex, left, the arc points (left → right), right (a closed polygon:
                                       close it back to the apex); crust = the arc points alone (stroke it in red pen).
                                       Every sector is this same shape: cache it once per (r, n).
   layout(r, n, u) → [{i, x, y, rot, up, pos, from: {x, y, rot}, to: {x, y, rot}}]   one per sector (index i)
                                       A RIGID transform: a local point p goes to R(rot)·p + (x, y). Linear in u from
                                       `from` (u = 0: apex at the circle's centre (0, 0), rot = mid + π/2) to `to`
                                       (u = 1: the strip). Strip: position pos = 2m (the m-th top sector from the left)
                                       is crust UP (rot 0, apex on the strip's bottom line); pos = 2m + 1 (the m-th
                                       bottom sector from the left) is crust DOWN (rot π, apex on the top line).
                                       Neighbours share a full straight edge: no gap, no overlap. The strip is centred
                                       on (0, 0) (its straight-edge band runs from y = −r·cos h / 2 to + r·cos h / 2;
                                       the crust bumps stick out above and below). Ease u yourself if you want easing.
                                       Apply in canvas: g.translate(ox + x·k, oy + y·k); g.rotate(rot); draw shape × k.
   strip(r, n) → {length, height, band, box: {w, h}, left, right}
                                       length = n·r·sin(π/n) (the strip's length along its straight-edge band; → π·r),
                                       height = r (the slant height of every sector = the rectangle's height in the
                                       limit), band = r·cos(π/n) (the straight-edge band's height), box = the bounding
                                       box (w = length + r·sin h, h = r·(2 − cos h)), left / right = the band's x range
                                       at mid-height.
   ---- area ----
   radiusSquares(r) → {outer: 4r², inner: 2r², area: πr², count: π, sq: r²}
   gridCount(r) → {whole, edge, edgeArea, total, cells: [{i, j, kind: 'whole' | 'edge', area}]}
                                       unit squares [i, i+1] × [j, j+1] of a grid with a vertex at the centre (0, 0):
                                       whole = fully inside, edge = the rim crosses them (positive area inside), edgeArea =
                                       the exact area of the disc inside the edge squares (analytic), total = whole +
                                       edgeArea (= πr² to ~1e-12).
   scale(k) → {across: k, around: k, inside: k·k}
   pizzas(a, b) → {a: side, b: side, diff: {k, pi, inPi, exact, at314, v314, atPi, vpi}, bigger: 'a' | 'b' | 'same',
                   small: {d, k, pi, at314}, fraction: {n, d, text, value}, gapOfSmall, coveredOfBig}
                   a, b = deals: an array of diameters ([12, 12]), one number, or a comma list ('12,12': core/bank.js
                   formulas cannot write [ ]). side = {diameters, radii, k (Σ r², exact via hundredths), pi = inPi: "72π",
                   exact: kπ, at314 = v314: 226.08 (3.14 × k in hundredths), atPi = vpi: 226.19 (k·π to hundredths),
                   each: [{d, r, rr: r², at314: 3.14·r²}]}. gapOfSmall = diff.k ÷ one pizza of a (0.25);
                   coveredOfBig = a.k ÷ b.k (8/9). diff = |a − b|; small = the first
                   pizza of a; fraction = diff.k / small.k reduced ("1/4"). pizzas([12, 12], [18]) → 72π vs 81π,
                   9π = 1/4 of 36π. Each side also has atPi (k·π to hundredths: 226.19, 254.47).
   crust(a, b) → {a: edge, b: edge, diff: {k, pi, inPi, exact, at314, v314, atPi, vpi}, more: 'a' | 'b' | 'same',
                  extra: {n, d, text, value}, moreOfA}   the total edge length of each deal (facts row 6.15):
                  edge = {diameters, k (Σ d), pi = inPi: "24π", exact, at314 = v314: 3.14·k (75.36), atPi = vpi: k·π (75.4)};
                  moreOfA = (a.k − b.k) ÷ b.k (1/3);
                  extra = how much more crust the bigger side has, as a fraction of the smaller (reduced): crust([12, 12],
                  [18]) → 24π vs 18π, a has 1/3 more.
   ---- pool helpers (core/bank.js must be loaded: Bank.fill / Bank.eval / Bank.format) ----
   itemSteps(item, params) → [{i, type, text, hint, format, value, answerText, options, correct, last}]
                   numpad: value = Bank.eval(step.formula, params), answerText = Bank.format(value, format);
                   choice: options filled, correct as given.
   ========================================================================== */
(function (root) {
  const PI = Math.PI, PI314 = 3.14, TAU = 2 * PI;

  /* ---------------- exact-ish decimals: integer arithmetic ---------------- */
  // a finite number → {m, s}: the integer m and the power of ten s with x = m / s (from its decimal string; ≤ 9 places)
  function dec(x) {
    x = +x;
    if (!isFinite(x)) throw new Error(`circle: ${x} is not a finite number`);
    const str = Number(x.toPrecision(12)).toString();
    if (/e/i.test(str)) throw new Error(`circle: ${x} is too small or large`);
    const k = Math.min(9, (str.split('.')[1] || '').length);     // a long float (37.699111…) keeps 9 places
    const s = Math.pow(10, k);
    return { m: Math.round(x * s), s };
  }
  // num / den rounded half away from zero to an integer (num, den integers, den > 0)
  function roundDiv(num, den) {
    const neg = num < 0, a = Math.abs(num);
    const q = Math.floor(a / den), r = a - q * den;
    const v = 2 * r >= den ? q + 1 : q;
    return neg ? -v : v;
  }
  const hundredths = x => { x = +x; return Math.sign(x) * Math.round(Number((Math.abs(x) * 100).toPrecision(12))) + 0; };   // like core/bank.js
  const C314h = d => { const { m, s } = dec(d); return roundDiv(314 * m, s); };               // 3.14·d in hundredths
  const A314h = r => { const { m, s } = dec(r); return roundDiv(314 * m * m, s * s); };       // 3.14·r² in hundredths
  const C314 = d => C314h(d) / 100;
  const A314 = r => A314h(r) / 100;
  const Cpih = d => hundredths(PI * d), Apih = r => hundredths(PI * r * r);
  const Cpi = d => Cpih(d) / 100, Api = r => Apih(r) / 100;
  const C = d => PI * d;
  const A = r => PI * r * r;
  const half = d => d / 2, twice = r => 2 * r, sq = x => x * x;

  const minus = s => String(s).replace(/^-/, '−');
  function num(v) {                                  // ≤ 2 decimals, zeros trimmed
    const h = hundredths(v), neg = h < 0, a = Math.abs(h), ip = Math.floor(a / 100), fp = a % 100;
    let t = String(ip); if (fp) t += '.' + String(fp).padStart(2, '0').replace(/0$/, '');
    return (neg && h ? '−' : '') + t;
  }
  function fix2(v) { const h = hundredths(v), a = Math.abs(h); return (h < 0 ? '−' : '') + Math.floor(a / 100) + '.' + String(a % 100).padStart(2, '0'); }
  const digits = (v, n) => minus((+v).toFixed(n));
  function inPi(k) {
    const t = num(k);
    if (t === '0' || t === '−0') return '0';
    if (t === '1') return 'π';
    if (t === '−1') return '−π';
    return t + 'π';
  }

  /* ---------------- rolling ---------------- */
  const turn = d => PI * d;
  const angle = (d, s) => 2 * s / d;
  function mark(d, s) {
    const r = d / 2, th = angle(d, s);
    const x = s - r * Math.sin(th), y = -r + r * Math.cos(th);
    return { x, y, cx: s, cy: -r, angle: th, onFloor: Math.abs(y) < 1e-9 };
  }
  function stamps(d, s) {
    const T = turn(d), out = [];
    if (!(s >= 0)) return [0];
    const n = Math.floor(s / T + 1e-9);
    for (let k = 0; k <= n; k++) out.push(k * T);
    return out;
  }
  function sticks(d) {
    const whole = Math.floor(PI), rest = PI - whole;
    return { whole, rest, restLength: rest * d, turn: turn(d), d };
  }

  /* ---------------- the shelf (DESIGN.md §5.1 step 5; sizes from docs/topics/facts-circles.md §6) ---------------- */
  // perSquare: units per 40-px grid square at the drawing scale (the scale the shelf card states)
  const OBJECTS = [
    { id: 'quarter', name: 'a US quarter', short: 'quarter', d: 0.955, unit: 'in', unitName: 'inch', units: 'inches', row: '6.1', perSquare: 0.1, invented: false, nominal: false, ball: false },
    { id: 'pizza', name: 'a 12-inch pizza', short: 'pizza', d: 12, unit: 'in', unitName: 'inch', units: 'inches', row: '6.15 (an invented example; "12-inch" = the diameter)', perSquare: 1.5, invented: true, nominal: false, ball: false },
    { id: 'rim', name: 'a basketball rim', short: 'rim', d: 18, unit: 'in', unitName: 'inch', units: 'inches', row: '6.2', perSquare: 2, invented: false, nominal: false, ball: false },
    { id: 'spaceship', name: 'Spaceship Earth', short: 'Spaceship Earth', d: 165, unit: 'ft', unitName: 'foot', units: 'feet', row: '6.11', perSquare: 20, invented: false, nominal: false, ball: true },
  ];
  const WHEEL = { id: 'bike', name: 'a 26-inch bike wheel', short: 'bike wheel', d: 26, unit: 'in', unitName: 'inch', units: 'inches', row: '6.14, 4.2', perSquare: 4, invented: false, nominal: true, ball: false };
  for (const o of [...OBJECTS, WHEEL]) {
    o.px = 40 / o.perSquare;
    o.scaleText = `1 square = ${num(o.perSquare)} ${o.perSquare === 1 ? o.unitName : o.units}`;
    Object.freeze(o);
  }
  Object.freeze(OBJECTS);
  const obj = id => { const o = id === WHEEL.id ? WHEEL : OBJECTS.find(x => x.id === id); if (!o) throw new Error(`circle: no shelf object "${id}"`); return o; };
  const D = id => obj(id).d;
  function row(o, { ratioDigits = 2 } = {}) {
    if (typeof o === 'string') o = obj(o);
    const across = +o.d, around = C(across), ratio = around / across;
    const u = o.unit ? ' ' + o.unit : '';
    return { across: minus(String(across)) + u, around: fix2(around) + u, ratio: digits(ratio, ratioDigits), ratio4: digits(ratio, 4), values: { across, around, ratio } };
  }

  /* ---------------- sectors ---------------- */
  function checkN(n) { if (!Number.isInteger(n) || n < 2 || n % 2) throw new Error(`circle: n must be an even whole number ≥ 2 (got ${n})`); }
  function sectors(r, n) {
    checkN(n);
    const h = PI / n, out = [];
    for (let i = 0; i < n; i++) { const a0 = -PI + 2 * i * h, a1 = a0 + 2 * h; out.push({ i, a0, a1, mid: a0 + h, half: h, area: h * r * r }); }
    return out;
  }
  function shape(r, n, { arc = 24 } = {}) {
    checkN(n);
    const h = PI / n, sh = Math.sin(h), ch = Math.cos(h);
    const left = [-r * sh, -r * ch], right = [r * sh, -r * ch], crust = [];
    for (let k = 0; k <= arc; k++) { const a = -PI / 2 - h + 2 * h * k / arc; crust.push([r * Math.cos(a), r * Math.sin(a)]); }
    crust[0] = left.slice(); crust[arc] = right.slice();
    return { half: h, apex: [0, 0], left, right, outline: [[0, 0], ...crust], crust };
  }
  function stripPose(r, n) {                        // u = 1 transforms, indexed by sector i
    const h = PI / n, rs = r * Math.sin(h), rc = r * Math.cos(h), half = n / 2;
    const ox = (n - 1) * rs / 2, oy = -rc / 2;       // centre the band on (0, 0)
    const to = new Array(n);
    for (let i = 0; i < n; i++) {
      if (i < half) { const pos = 2 * i; to[i] = { pos, up: true, x: pos * rs - ox, y: 0 - oy, rot: 0 }; }
      else { const m = n - 1 - i, pos = 2 * m + 1; to[i] = { pos, up: false, x: pos * rs - ox, y: -rc - oy, rot: PI }; }
    }
    return to;
  }
  function layout(r, n, u) {
    const S = sectors(r, n), to = stripPose(r, n), k = Math.max(0, Math.min(1, +u || 0));
    return S.map(s => {
      const from = { x: 0, y: 0, rot: s.mid + PI / 2 }, t = to[s.i];
      return { i: s.i, x: from.x + (t.x - from.x) * k, y: from.y + (t.y - from.y) * k, rot: from.rot + (t.rot - from.rot) * k,
        up: t.up, pos: t.pos, from, to: { x: t.x, y: t.y, rot: t.rot } };
    });
  }
  function strip(r, n) {
    checkN(n);
    const h = PI / n, rs = r * Math.sin(h), rc = r * Math.cos(h), length = n * rs;
    return { length, height: r, band: rc, box: { w: length + rs, h: r * (2 - Math.cos(h)) }, left: -length / 2, right: length / 2 };
  }
  // apply a layout transform to a local point (tests, hit tests)
  const place = (tf, p) => { const c = Math.cos(tf.rot), s = Math.sin(tf.rot); return [c * p[0] - s * p[1] + tf.x, s * p[0] + c * p[1] + tf.y]; };
  // a screen point → that sector's local frame
  const unplace = (tf, p) => { const dx = p[0] - tf.x, dy = p[1] - tf.y, c = Math.cos(tf.rot), s = Math.sin(tf.rot); return [c * dx + s * dy, -s * dx + c * dy]; };

  /* ---------------- area ---------------- */
  const radiusSquares = r => ({ outer: 4 * r * r, inner: 2 * r * r, area: A(r), count: PI, sq: r * r });
  // the exact area of the disc x² + y² ≤ r² inside the rectangle [x0, x1] × [y0, y1]
  function discRect(r, x0, x1, y0, y1) {
    const a = Math.max(x0, -r), b = Math.min(x1, r);
    if (a >= b || y0 >= y1) return 0;
    const sOf = x => Math.sqrt(Math.max(0, r * r - x * x));
    const Sint = x => 0.5 * (x * sOf(x) + r * r * Math.asin(Math.max(-1, Math.min(1, x / r))));   // ∫ √(r² − x²) dx
    const bp = [a, b];
    for (const y of [y0, y1]) if (Math.abs(y) < r) { const x = Math.sqrt(r * r - y * y); for (const v of [-x, x]) if (v > a && v < b) bp.push(v); }
    bp.sort((p, q) => p - q);
    let area = 0;
    for (let k = 0; k + 1 < bp.length; k++) {
      const p = bp[k], q = bp[k + 1]; if (q - p <= 0) continue;
      const m = (p + q) / 2, s = sOf(m);
      const top = Math.min(y1, s), bot = Math.max(y0, -s);
      if (top <= bot) continue;
      // ∫ top − ∫ bot over [p, q]; top is y1 or s(x), bot is y0 or −s(x) (constant choice inside the piece)
      const It = y1 <= s ? y1 * (q - p) : Sint(q) - Sint(p);
      const Ib = y0 >= -s ? y0 * (q - p) : -(Sint(q) - Sint(p));
      area += It - Ib;
    }
    return area;
  }
  function gridCount(r) {
    const n = Math.ceil(r), cells = [];
    let whole = 0, edge = 0, edgeArea = 0;
    for (let i = -n; i < n; i++) for (let j = -n; j < n; j++) {
      const far = Math.max(Math.hypot(i, j), Math.hypot(i + 1, j), Math.hypot(i, j + 1), Math.hypot(i + 1, j + 1));
      if (far <= r) { whole++; cells.push({ i, j, kind: 'whole', area: 1 }); continue; }
      const a = discRect(r, i, i + 1, j, j + 1);
      if (a > 1e-12) { edge++; edgeArea += a; cells.push({ i, j, kind: 'edge', area: a }); }
    }
    return { whole, edge, edgeArea, total: whole + edgeArea, cells };
  }
  const scale = k => ({ across: k, around: k, inside: k * k });

  /* ---------------- the bet ---------------- */
  const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
  function side(ds) {
    // k = Σ r² in exact ten-thousandths (r = d/2 has ≤ 3 decimals for d with ≤ 2)
    const each = ds.map(d => { const r = d / 2; return { d, r, rr: r * r, rrU: rr4(r), at314: A314(r) }; });
    const kU = each.reduce((s, e) => s + e.rrU, 0), k = kU / 1e4;
    // 3.14 × k in hundredths: 314 · kU / 1e4 → hundredths
    const at314h = roundDiv(314 * kU, 1e4);
    const at314 = at314h / 100, atPi = hundredths(PI * k) / 100;
    return { diameters: ds.slice(), radii: each.map(e => e.r), k, kU, pi: inPi(k), inPi: inPi(k), exact: PI * k, at314, v314: at314, atPi, vpi: atPi, each: each.map(({ rrU, ...e }) => e) };
  }
  function rr4(r) { const { m, s } = dec(r); if (s > 100) throw new Error('circle.pizzas: radii with ≤ 2 decimals'); return m * m * (100 / s) * (100 / s); }   // r² in ten-thousandths
  // a deal: an array of diameters, one number, or a comma list ("12,12": core/bank.js formulas cannot write [ ])
  const deal = v => (Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [v]).map(Number).filter(x => isFinite(x) && x > 0);
  function pizzas(a, b) {
    a = deal(a); b = deal(b);
    const A_ = side(a), B_ = side(b);
    const dU = Math.abs(A_.kU - B_.kU), small = side([a[0]]), g = gcd(dU, small.kU);
    const out = {
      a: strip_(A_), b: strip_(B_),
      diff: { k: dU / 1e4, pi: inPi(dU / 1e4), inPi: inPi(dU / 1e4), exact: PI * dU / 1e4, at314: roundDiv(314 * dU, 1e4) / 100, v314: roundDiv(314 * dU, 1e4) / 100,
        atPi: hundredths(PI * dU / 1e4) / 100, vpi: hundredths(PI * dU / 1e4) / 100 },
      bigger: A_.kU === B_.kU ? 'same' : A_.kU > B_.kU ? 'a' : 'b',
      small: { d: a[0], k: small.k, pi: small.pi, at314: small.at314 },
      fraction: { n: dU / g, d: small.kU / g, text: small.kU / g === 1 ? String(dU / g) : `${dU / g}/${small.kU / g}`, value: dU / small.kU },
      gapOfSmall: dU / small.kU,                         // the difference as a fraction of ONE pizza of deal a (0.25)
      coveredOfBig: B_.kU ? A_.kU / B_.kU : null,        // k of a ÷ k of b (72/81 = 8/9)
    };
    return out;
  }
  const strip_ = s => { const { kU, ...o } = s; return o; };
  // the crust: Σ d per deal (circumference = π·d), exact in hundredths of an inch of diameter
  function crust(a, b) {
    a = deal(a); b = deal(b);
    const edge = ds => { const kH = ds.reduce((t, d) => t + hundredths(d), 0), k = kH / 100, at314 = roundDiv(314 * kH, 100) / 100, atPi = hundredths(PI * k) / 100;
      return { diameters: ds.slice(), k, kH, pi: inPi(k), inPi: inPi(k), exact: PI * k, at314, v314: at314, atPi, vpi: atPi }; };
    const A_ = edge(a), B_ = edge(b), dH = Math.abs(A_.kH - B_.kH), lo = Math.min(A_.kH, B_.kH), g = gcd(dH, lo || 1);
    const drop = e => { const { kH, ...o } = e; return o; };
    return {
      a: drop(A_), b: drop(B_),
      diff: { k: dH / 100, pi: inPi(dH / 100), inPi: inPi(dH / 100), exact: PI * dH / 100, at314: roundDiv(314 * dH, 100) / 100, v314: roundDiv(314 * dH, 100) / 100,
        atPi: hundredths(PI * dH / 100) / 100, vpi: hundredths(PI * dH / 100) / 100 },
      more: A_.kH === B_.kH ? 'same' : A_.kH > B_.kH ? 'a' : 'b',
      extra: lo ? { n: dH / g, d: lo / g, text: lo / g === 1 ? String(dH / g) : `${dH / g}/${lo / g}`, value: dH / lo } : null,
      moreOfA: B_.kH ? (A_.kH - B_.kH) / B_.kH : null,   // (a − b) ÷ b: 1/3 for two 12-inch vs one 18-inch
    };
  }

  /* ---------------- pool helpers (core/bank.js) ---------------- */
  function itemSteps(item, params) {
    const B = root.Bank;
    if (!B) throw new Error('circle.itemSteps: core/bank.js is not loaded');
    const S = item.steps || [];
    return S.map((s, i) => {
      const o = { i, type: s.type, text: B.fill(s.text || '', params), hint: s.hint ? B.fill(s.hint, params) : '', format: s.format || null, last: i === S.length - 1 };
      if (s.type === 'numpad') { o.value = B.eval(String(s.formula), params); o.answerText = B.format(o.value, s.format || 'num'); }
      else if (s.type === 'choice') { o.options = (s.options || []).map(x => B.fill(String(x), params)); o.correct = s.correct; o.answerText = o.options[o.correct]; }
      return o;
    });
  }

  const Circle = {
    PI, PI314, TAU,
    C, A, C314, A314, C314h, A314h, Cpi, Api, Cpih, Apih, inPi, half, twice, sq, num, fix2, digits, hundredths, roundDiv,
    turn, angle, mark, stamps, sticks,
    objects: OBJECTS, wheel: WHEEL, obj, D, row,
    sectors, shape, layout, strip, place, unplace,
    radiusSquares, gridCount, discRect, scale,
    pizzas, crust,
    itemSteps,
  };
  root.Circle = Circle;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('circle', Circle);
  if (typeof module !== 'undefined' && module.exports) module.exports = Circle;
})(typeof window !== 'undefined' ? window : globalThis);
